/**
 * AccessibilityWidget - Main controller
 * Entry point for the bundled widget
 */
import StorageManager from '../Core/StorageManager.js';
import EventManager from '../Core/EventManager.js';
import I18nManager from '../Core/I18nManager.js';
import Panel from '../UI/Panel.js';
import ControlBar from '../UI/ControlBar.js';
import ProfileManager from '../UI/ProfileManager.js';

// Feature modules
import ContrastFeature from '../Features/contrast.js';
import FontSizingFeature from '../Features/font-sizing.js';
import HighlightFeature from '../Features/highlight.js';
import TextReaderFeature from '../Features/text-reader.js';
import ReadingGuideFeature from '../Features/reading-guide.js';
import MagnifierFeature from '../Features/magnifier.js';
import ColorCustomizerFeature from '../Features/color-customizer.js';
import CursorCustomizerFeature from '../Features/cursor-customizer.js';
import ImageDescriptionsFeature from '../Features/image-descriptions.js';
import EnlargeButtonsFeature from '../Features/enlarge-buttons.js';
import ReadableModeFeature from '../Features/readable-mode.js';
import MediaControlsFeature from '../Features/media-controls.js';
import ScreenReaderFeature from '../Features/screen-reader.js';
import KeyboardNavigationFeature from '../Features/keyboard-navigation.js';
import ReadFocusFeature from '../Features/read-focus.js';

export default class AccessibilityWidget {
    constructor(config = {}) {
        this.config = config;
        this.features = {};
        this.activeFeatures = new Set();

        // Core services
        this.storage = new StorageManager(config.storage || {});
        this.events = new EventManager();

        // Parse language config: 'de,en' → default='de', available=['de','en']
        let defaultLang = 'en';
        let availLangs = ['en', 'de'];
        if (config.language && typeof config.language === 'string') {
            const parts = config.language.split(',').map(s => s.trim()).filter(Boolean);
            if (parts.length > 0) {
                defaultLang = parts[0];
                availLangs = parts;
            }
        } else if (config.language) {
            defaultLang = config.language;
        }

        this.i18n = new I18nManager({
            language: defaultLang,
            availableLanguages: config.availableLanguages || availLangs
        });

        // Restore saved language
        const savedLang = this.storage.get('language');
        if (savedLang) {
            this.i18n.setLanguage(savedLang);
        }

        // Initialize when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this._init());
        } else {
            this._init();
        }
    }

    /**
     * Initialize the widget
     */
    _init() {
        if (!this.config.enabled) return;

        this._cleanupLegacyCookies();
        this._applyColorConfig();
        this._registerFeatures();
        this._createUI();
        this._restoreStates();
        this._bindGlobalShortcuts();

        // Callback
        if (typeof this.config.onInit === 'function') {
            this.config.onInit();
        }

        this.events.emit('widget:init');

        if (this.config.debug) {
            console.log('[A11Y] Widget initialized with', Object.keys(this.features).length, 'features');
        }
    }

    /**
     * Remove legacy easyView cookies that interfere with the widget
     */
    _cleanupLegacyCookies() {
        const legacyCookies = ['normalContrast', 'currentRatio'];
        legacyCookies.forEach(name => {
            document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        });
    }

    /**
     * Apply color config from TypoScript to CSS custom properties
     */
    _applyColorConfig() {
        const colors = this.config.colors || {};
        const root = document.documentElement;
        if (colors.primary) {
            root.style.setProperty('--dhA11y-primary', colors.primary);
            // Generate hover color (darken by ~15%)
            root.style.setProperty('--dhA11y-primary-hover', this._darkenColor(colors.primary, 0.15));
        }
        if (colors.secondary) {
            root.style.setProperty('--dhA11y-secondary', colors.secondary);
        }
        if (colors.danger) {
            root.style.setProperty('--dhA11y-danger', colors.danger);
        }
        if (colors.text) {
            root.style.setProperty('--dhA11y-text', colors.text);
        }
    }

    /**
     * Darken a hex color by a given amount (0-1)
     */
    _darkenColor(hex, amount) {
        hex = hex.replace('#', '');
        const r = Math.max(0, Math.round(parseInt(hex.substring(0, 2), 16) * (1 - amount)));
        const g = Math.max(0, Math.round(parseInt(hex.substring(2, 4), 16) * (1 - amount)));
        const b = Math.max(0, Math.round(parseInt(hex.substring(4, 6), 16) * (1 - amount)));
        return '#' + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
    }

    /**
     * Register all feature modules
     */
    _registerFeatures() {
        const featureConfig = this.config.features || {};
        const featureSettings = this.config.featureSettings || {};

        const featureMap = {
            // Navigation
            screenReader: { cls: ScreenReaderFeature, group: 'navigation' },
            keyboardNavigation: { cls: KeyboardNavigationFeature, group: 'navigation' },
            textReader: { cls: TextReaderFeature, group: 'navigation' },
            readFocus: { cls: ReadFocusFeature, group: 'navigation' },

            // Color
            monochrome: { cls: ContrastFeature, group: 'color', mode: 'monochrome' },
            darkHighContrast: { cls: ContrastFeature, group: 'color', mode: 'darkHighContrast' },
            brightHighContrast: { cls: ContrastFeature, group: 'color', mode: 'brightHighContrast' },
            contrastMode: { cls: ContrastFeature, group: 'color', mode: 'contrastMode' },
            lowSaturation: { cls: ColorCustomizerFeature, group: 'color', mode: 'lowSaturation' },
            highSaturation: { cls: ColorCustomizerFeature, group: 'color', mode: 'highSaturation' },
            customColors: { cls: ColorCustomizerFeature, group: 'color', mode: 'customColors' },

            // Content
            fontSizing: { cls: FontSizingFeature, group: 'content' },
            cursorCustomization: { cls: CursorCustomizerFeature, group: 'content' },

            // Additional
            readingGuide: { cls: ReadingGuideFeature, group: 'additional' },
            magnifier: { cls: MagnifierFeature, group: 'additional' },
            readableFont: { cls: FontSizingFeature, group: 'additional', mode: 'readableFont' },
            imageDescriptions: { cls: ImageDescriptionsFeature, group: 'additional' },
            highlightLinks: { cls: HighlightFeature, group: 'additional', mode: 'links' },
            highlightHeaders: { cls: HighlightFeature, group: 'additional', mode: 'headers' },
            highlightElements: { cls: HighlightFeature, group: 'additional', mode: 'elements' },
            enlargeButtons: { cls: EnlargeButtonsFeature, group: 'additional' },
            readableMode: { cls: ReadableModeFeature, group: 'additional' },
            muteMedia: { cls: MediaControlsFeature, group: 'additional' }
        };

        for (const [key, def] of Object.entries(featureMap)) {
            if (featureConfig[key] === false || featureConfig[key] === 0 || featureConfig[key] === '0') continue;

            try {
                const settings = featureSettings[key] || {};
                this.features[key] = new def.cls(this, {
                    key,
                    group: def.group,
                    mode: def.mode || null,
                    ...settings
                });
            } catch (e) {
                console.error(`[A11Y] Failed to register feature: ${key}`, e);
            }
        }
    }

    /**
     * Create UI components
     */
    _createUI() {
        // Main panel
        this.panel = new Panel(this);

        // Profile manager
        if (this.config.profiles && this.config.profiles.enabled !== false) {
            this.profileManager = new ProfileManager(this);
        }

        // Control bar
        if (this.config.controlBar && this.config.controlBar.enabled !== false) {
            this.controlBar = new ControlBar(this);
        }
    }

    /**
     * Restore saved feature states from storage
     */
    _restoreStates() {
        const states = this.storage.getAllFeatureStates();
        for (const [key, active] of Object.entries(states)) {
            if (active && this.features[key]) {
                this.toggleFeature(key, true, false);
            }
        }
    }

    /**
     * Bind global keyboard shortcuts
     */
    _bindGlobalShortcuts() {
        document.addEventListener('keydown', (e) => {
            // Ctrl+Alt+A: Toggle widget panel
            if (e.ctrlKey && e.altKey && e.code === 'KeyA') {
                e.preventDefault();
                this.panel.toggle();
            }
            // Escape: Close panel
            if (e.code === 'Escape' && this.panel && this.panel.isOpen) {
                this.panel.close();
            }
        });
    }

    /**
     * Toggle a feature on/off
     */
    toggleFeature(key, forceState = null, persist = true) {
        const feature = this.features[key];
        if (!feature) return false;

        const newState = forceState !== null ? forceState : !this.activeFeatures.has(key);

        if (newState) {
            if (feature.options && feature.options.group === 'color') {
                for (const otherKey of Array.from(this.activeFeatures)) {
                    if (otherKey === key) continue;
                    const otherFeature = this.features[otherKey];
                    if (otherFeature && otherFeature.options && otherFeature.options.group === 'color') {
                        this.toggleFeature(otherKey, false, persist);
                    }
                }
            }
            feature.enable();
            this.activeFeatures.add(key);

            if (key === 'textReader') {
                this.storage.set('textReader_autoPlay', true);
                if (this.features.textReader && typeof this.features.textReader.play === 'function') {
                    this.features.textReader.play();
                }
            }
        } else {
            feature.disable();
            this.activeFeatures.delete(key);

            if (key === 'textReader') {
                this.storage.set('textReader_autoPlay', false);
            }
        }

        // Persist state
        if (persist) {
            this.storage.set('feature_' + key, newState);

            // Deactivate active profile when user manually toggles a feature
            if (this.profileManager && this.profileManager.activeProfile) {
                this.profileManager.activeProfile = null;
                this.profileManager._updateProfileCards();
                this.storage.remove('active_profile');
            }
        }

        // Update UI
        this.events.emit('feature:toggle', { key, active: newState });

        // Callback
        if (typeof this.config.onFeatureToggle === 'function') {
            this.config.onFeatureToggle(key, newState);
        }

        return newState;
    }

    /**
     * Check if a feature is active
     */
    isFeatureActive(key) {
        return this.activeFeatures.has(key);
    }

    /**
     * Reset all features to default
     */
    resetAll() {
        // Reset active profile first
        if (this.profileManager) {
            this.profileManager.activeProfile = null;
            this.profileManager._updateProfileCards();
        }

        for (const key of this.activeFeatures) {
            if (this.features[key]) {
                this.features[key].disable();
            }
        }
        this.activeFeatures.clear();
        this.storage.clearAll();

        // Re-save language preference
        this.storage.set('language', this.i18n.getLanguage());

        this.events.emit('widget:reset');

        // Update UI
        if (this.panel) {
            this.panel.updateAllCards();
        }
    }

    /**
     * Change widget language
     */
    setLanguage(lang) {
        if (this.i18n.setLanguage(lang)) {
            this.storage.set('language', lang);
            this.events.emit('language:change', { language: lang });
            if (this.panel) {
                this.panel.refresh();
            }
            return true;
        }
        return false;
    }

    /**
     * Destroy widget and clean up
     */
    destroy() {
        // Disable all active features
        for (const key of this.activeFeatures) {
            if (this.features[key]) {
                this.features[key].disable();
            }
        }

        // Remove UI
        if (this.panel) this.panel.destroy();
        if (this.controlBar) this.controlBar.destroy();

        // Clean up events
        this.events.removeAll();

        this.events.emit('widget:destroy');
    }
}

// Auto-initialize when loaded via TYPO3 TypoScript or loader.js
// Reads config from window.accessibilityWidget
(function () {
    var config = window.accessibilityWidget || {};
    if (config.enabled === false) return;

    function init() {
        if (window.AccessibilityWidgetInstance) return;
        window.AccessibilityWidgetInstance = new AccessibilityWidget(config);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
